<script lang="ts">
    import type {ComponentSize} from '../types';
    import {createEventDispatcher} from 'svelte';
    import {exists, uuid} from 'shared/utils/custom-utils';

    export let id: string = uuid();
    export let checked = false;
    export let indeterminate = false;
    export let size: ComponentSize = 'md';
    export let disabled = false;

    const dispatch = createEventDispatcher<{ change: { checked: boolean } }>();

    let inputRef: HTMLInputElement;

    function changeHandler() {
        dispatch('change', {checked: inputRef.checked})
    }

    export function getInputRef(): HTMLInputElement {
        return inputRef;
    }

    function indeterminateState(node: HTMLInputElement, initialIndeterminateValue: boolean) {
        node.indeterminate = initialIndeterminateValue;
        return {
            update: (updatedIndeterminateValue: boolean) => node.indeterminate = updatedIndeterminateValue
        }
    }
</script>

<div class="checkbox-container size-{size}" data-checked={checked}>
    <input {id}
           type="checkbox"
           bind:checked
           {disabled}
           {...$$restProps}
           use:indeterminateState={indeterminate}
           on:change={changeHandler}
           bind:this={inputRef}>

    {#if exists($$slots.default)}
        <!-- tabindex="-1" to make focusout target label instead of body (for form controls touched detection) -->
        <label for={id} tabindex="-1">
            <slot/>
        </label>
    {/if}
</div>

<style lang="less" global>
    @import (reference) "styles/portaro.themes.less";
    @import (reference) "styles/portaro.media-queries.less";

    @checkbox-size: 1.42857143em; // (normally equals 20px)
    @disabled-opacity: 0.65;
    @default-max-width: 100%;
    @state-danger-text: #E42C42;
    @color: var(--accent-blue);

    .checkbox-custom-focus() {
        outline: none;
        border-color: @color;
        box-shadow: none;
    }

    .checkbox-container {
        display: inline-block;
        line-height: @checkbox-size;
        max-width: var(--checkbox-max-width, @default-max-width);

        &.size-sm, &.size-xs {
            font-size: 12px;
        }

        &.size-lg {
            font-size: 20px;
        }

        label {
            margin-bottom: 0;
            font-weight: 400;
            cursor: pointer;
            @media screen and (max-width: @screen-sm-min) {
                & {
                    max-width: 90%;
                }
            }
        }

        input[type="checkbox"]:disabled ~ label {
            opacity: @disabled-opacity;
            cursor: default;
        }

        input[type="checkbox"] {
            -webkit-appearance: none;
            appearance: none;
            position: relative;
            display: inline-block;
            line-height: @checkbox-size;
            height: @checkbox-size;
            width: @checkbox-size;
            flex-shrink: 0;
            margin: 0;
            vertical-align: top;
            text-align: center;
            background-color: @themed-body-bg;
            border: 1px solid @themed-border-default;
            border-radius: 0.25em;
            transition: border-color ease-in-out 0.25s, background-color ease 0.25s;
            cursor: pointer;

            &:before {
                top: 0;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                align-items: center;

                font-family: uicons-bold-rounded !important;
                font-style: normal;
                font-weight: normal !important;
                font-variant: normal;
                text-transform: none;
                line-height: 1;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
            }

            &:focus {
                .checkbox-custom-focus();
            }

            &:checked {
                border-color: var(--accent-blue);
                background-color: var(--accent-blue);
                color: #FFF;

                &:before {
                    content: "\fb38";
                }
            }

            &:indeterminate {
                &:before {
                    content: "\f62e";
                }
            }

            &:disabled {
                opacity: @disabled-opacity;
                cursor: not-allowed;
            }
        }
    }

    .has-error {
        .checkbox-container {
            label {
                color: var(--danger-red);
            }

            input[type="checkbox"] {
                border-color: rgba(0, 0, 0, 0.2);
                box-shadow: none;

                &:checked {
                    border-color: rgba(0, 0, 0, 0.2);
                }

                &:focus {
                    border-color: rgba(0, 0, 0, 0.2);
                }
            }
        }

        /* reset error styles if editor is nested in meta-editor which has error */

        .has-not-error {
            .checkbox-container {
                label {
                    color: initial;
                }

                input[type="checkbox"] {
                    border-color: @themed-border-default;
                    box-shadow: none;

                    &:checked {
                        border-color: rgba(0, 0, 0, 0.2);;
                    }

                    &:focus {
                        .checkbox-custom-focus();
                    }
                }
            }
        }
    }
</style>